import { Calendar } from "lucide-react";

import { Button } from "@/components/ui/button";

interface DiscoveryCardProps {
  title: string;
  body: string;
  /** The rule note footer (e.g. the compensation "model, not the math" line). */
  note?: string;
  /**
   * The single, shared book-a-discovery-call action. Bound to the same page-level
   * handler the header uses, so this never becomes a competing booking entry point.
   */
  onBookCall?: () => void;
}

/**
 * The discovery-call treatment — NOT an empty state, a confident product move used
 * wherever the org chose not to publish numbers (primarily compensation). Teal-tinted,
 * teal-bordered, with a calendar tile and the shared "Book a discovery call" action.
 */
const DiscoveryCard = ({ title, body, note, onBookCall }: DiscoveryCardProps) => (
  <div className="overflow-hidden rounded-2xl border border-primary bg-primary-tint">
    <div className="flex items-start gap-5 px-[26px] pb-6 pt-[26px]">
      <div className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full bg-primary">
        <Calendar className="h-[22px] w-[22px] text-primary-foreground" strokeWidth={1.8} />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="m-0 text-[17px] font-semibold tracking-[-0.01em] text-primary-pressed">{title}</h3>
        <p className="mt-[7px] max-w-[520px] text-[14.5px] leading-[1.6] text-ink-700">{body}</p>
        {/* No handler means the organization offered no scheduling link, and a
            button with nowhere to go is still an offer it never made. */}
        {onBookCall && (
          <div className="mt-[18px]">
            <Button className="gap-2" onClick={onBookCall}>
              <Calendar className="h-4 w-4" />
              Book a discovery call
            </Button>
          </div>
        )}
      </div>
    </div>
    {note && (
      <div className="border-t border-primary/20 bg-white/35 px-[26px] py-[11px] text-[12.5px] text-primary-pressed">
        {note}
      </div>
    )}
  </div>
);

export default DiscoveryCard;
